import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type ExportSummary } from "../api/client";
import { utc } from "../import/items";

const STATE: Record<
  string,
  { label: string; color: "default" | "info" | "success" | "error" }
> = {
  requested: { label: "Awaiting approval", color: "info" },
  rejected: { label: "Rejected", color: "default" },
  building: { label: "Packaging…", color: "info" },
  ready: { label: "Ready", color: "success" },
  failed: { label: "Failed", color: "error" },
};

const size = (b: number) =>
  b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${(b / 1e6).toFixed(1)} MB`;

/**
 * Evidence exports from a case (threat model §2.1): requested with a reason,
 * approved or rejected by someone else, then downloaded by the requester as a
 * signed BagIt package.
 */
export function Exports({
  caseId,
  canRequest,
  fileCount,
}: {
  caseId: string;
  canRequest: boolean;
  fileCount: number;
}) {
  const qc = useQueryClient();
  const key = ["cases", caseId, "exports"];
  const exports = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(
        api.GET("/api/cases/{id}/exports", {
          params: { path: { id: caseId } },
        }),
      ),
    refetchInterval: (q) =>
      q.state.data?.some((e) => e.state === "building") ? 2000 : false,
  });
  const signingKey = useQuery({
    queryKey: ["exports", "signing-key"],
    queryFn: () => unwrap(api.GET("/api/exports/signing-key")),
    retry: false,
  });
  const [reason, setReason] = useState("");
  const [derived, setDerived] = useState(false);
  const [deciding, setDeciding] = useState<{
    e: ExportSummary;
    approve: boolean;
  } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });

  const request = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/cases/{id}/exports", {
          params: { path: { id: caseId } },
          body: { reason, include_derived: derived },
        }),
      ),
    onSuccess: () => {
      setReason("");
      setDerived(false);
      refresh();
    },
  });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Exports"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Exports
      </Typography>
      {canRequest && fileCount > 0 && (
        <Stack
          component="form"
          direction="row"
          sx={{ gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
            request.mutate();
          }}
        >
          <TextField
            size="small"
            label="Reason for export"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 240 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={derived}
                onChange={(e) => setDerived(e.target.checked)}
              />
            }
            label="Include working copies"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!reason.trim() || request.isPending}
          >
            Request export of {fileCount} recording{fileCount === 1 ? "" : "s"}
          </Button>
        </Stack>
      )}
      {request.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {request.error.message}
        </Alert>
      )}
      {exports.data?.length === 0 ? (
        <Typography color="text.secondary">No exports yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Export requests">
            <TableHead>
              <TableRow>
                <TableCell>Requested (UTC)</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Decided by</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {exports.data?.map((e) => {
                const s = STATE[e.state] ?? {
                  label: e.state,
                  color: "default" as const,
                };
                return (
                  <TableRow key={e.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {e.requested_by}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", fontFamily: "monospace" }}
                      >
                        {utc(e.requested_at).slice(0, 16)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {e.reason}
                      <Typography variant="caption" sx={{ display: "block" }}>
                        {e.file_count} recording{e.file_count === 1 ? "" : "s"}
                        {e.include_derived && ", with working copies"}
                        {e.range &&
                          `, clips of ${utc(e.range.from).slice(0, 19)}–${utc(e.range.to).slice(11, 19)} UTC`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.label} color={s.color} />
                      {e.state === "ready" && e.size_bytes != null && (
                        <Typography variant="caption" sx={{ display: "block" }}>
                          {size(e.size_bytes)}
                        </Typography>
                      )}
                      {e.error && (
                        <Typography variant="caption" sx={{ display: "block" }}>
                          {e.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.decided_by ?? "—"}
                      {e.self_approved && " (self-approval)"}
                      {e.decision_note && (
                        <Typography variant="caption" sx={{ display: "block" }}>
                          {e.decision_note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {e.can_decide && (
                        <>
                          <Button
                            size="small"
                            onClick={() => setDeciding({ e, approve: true })}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => setDeciding({ e, approve: false })}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {e.can_download && (
                        <Button
                          size="small"
                          variant="contained"
                          href={`/api/exports/${e.id}/package`}
                        >
                          Download
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {signingKey.data && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, overflowWrap: "anywhere" }}
        >
          Packages are signed with a {signingKey.data.kind}. Give recipients its
          fingerprint separately, so they can check it (VERIFY.txt in the
          package says how):{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{ fontFamily: "monospace" }}
            aria-label="Signing key fingerprint"
          >
            {signingKey.data.fingerprint}
          </Typography>
        </Typography>
      )}
      {deciding && (
        <Decide
          {...deciding}
          onClose={() => setDeciding(null)}
          onDone={() => {
            setDeciding(null);
            refresh();
          }}
        />
      )}
    </Paper>
  );
}

function Decide({
  e,
  approve,
  onClose,
  onDone,
}: {
  e: ExportSummary;
  approve: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");
  const decide = useMutation({
    mutationFn: () =>
      unwrap(
        approve
          ? api.POST("/api/exports/{id}/approve", {
              params: { path: { id: e.id } },
              body: { note, password: e.needs_password ? password : null },
            })
          : api.POST("/api/exports/{id}/reject", {
              params: { path: { id: e.id } },
              body: { note, password: e.needs_password ? password : null },
            }),
      ),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {approve ? "Approve" : "Reject"} export requested by {e.requested_by}
      </DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          {e.file_count} recording{e.file_count === 1 ? "" : "s"}
          {e.include_derived ? ", with working copies" : ""}. Reason: {e.reason}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Note (recorded in the audit log)"
          value={note}
          onChange={(ev) => setNote(ev.target.value)}
          sx={{ mt: 1 }}
        />
        {e.needs_password && (
          <>
            <Alert severity="info" sx={{ mt: 2 }}>
              This is your own export. Enter your password to{" "}
              {approve ? "approve" : "reject"} it; it is recorded as a
              self-approval in the audit log and in the package.
            </Alert>
            <TextField
              fullWidth
              type="password"
              autoComplete="current-password"
              label="Your password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        )}
        {decide.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {decide.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={approve ? "primary" : "error"}
          disabled={decide.isPending || (e.needs_password && !password)}
          onClick={() => decide.mutate()}
        >
          {approve ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
